import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Output } from "valibot";

import ChatPanel from "components/ChatPanel";
import useFormattedWelcomeMessage from "components/ChatPanel/useFormattedWelcomeMessage";
import PopupMessages from "components/PopupMessages";
import WidgetButton from "components/WidgetButton";
import {
  getLocalStorageValue,
  getSessionStorageValue,
  getUserDataStorageKey,
  setSessionStorageValue,
} from "shared/functions";
import {
  useChatUserId,
  useConfigContext,
  useGlobalsContext,
  useIsBotDataCompletelyTrained,
} from "shared/hooks";
import { AppConfiguration, GenerationMode } from "shared/types";
import classNames from "shared/utils/classNames";
import replaceVariablesInString from "shared/utils/replaceVariablesInString";
import { getChatPanelText } from "shared/utils/sourceUtils";

import {
  AfterMessageQuestion,
  ChatDataSchema,
  LiveAgentHandoffInfo,
} from "./schemas";
import { openedInDashboard } from "./functions";
import useBotInfo from "./useBotInfo";
import useBotInfoUpdates from "./useBotInfoUpdates";
import useBotVariables from "./useBotVariables";
import useChatHistory from "./useChatHistory";
import useChatUserFormFields from "./useChatUserFormFields";
import useConnectSSE from "./useConnectSSE";
import useConnectWebsocket from "./useConnectWebsocket";
import useLocationBasedConsentInfo from "./useConsentIntegrationInfo";
import useEvents from "./useEvents";
import useHandleEvents from "./useHandleEvents";
import useInstalledApps from "./useInstalledApps";
import usePopupClosedByUser from "./usePopupClosedByUser";
import usePreserveChatData from "./usePreserveChatData";
import useScheduledActions from "./useScheduledActions";
import useStarterQuestions from "./useStarterQuestions";
import useStyleColors from "./useStyleColors";
import useVariablesMessageListener from "./useVariablesMessageListener";

import "./index.css";

type Props = {
  config: AppConfiguration;
};

const SonicBot = (props: Props) => {
  const {
    widgetOpen,
    widgetExpanded,
    setWidgetExpanded,
    setInAppMode,
    parentPostMessage,
    sendAnalyticsEvent,
    variables,
  } = useGlobalsContext();
  const { isIframe, instanceName, token } = useConfigContext();

  const { config } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeIdRef = useRef<NodeJS.Timeout>();
  const onSendRef = useRef<typeof onSend>();

  const [isRenderWidget, setIsRenderWidget] = useState(true);
  const [message, setMessage] = useState("");
  const [chatData, setChatData] = useState<Output<typeof ChatDataSchema>>([]);
  const [hasChatEnded, setHasChatEnded] = useState<boolean>(false);
  const [liveAgentHandoffInfo, setLiveAgentHandoffInfo] = useState<Output<
    typeof LiveAgentHandoffInfo
  > | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [generationMode, setGenerationMode] = useState<GenerationMode | null>(
    null,
  );
  const [useWebsocketGeneration, setUseWebsocketGeneration] = useState(false);

  // Delay for positioning
  const [renderPositionedWidget, setRenderPositionedWidget] = useState(false);

  const { locationBasedConsentInfo, loading: loadingLocationBasedConsentInfo } =
    useLocationBasedConsentInfo(config.token);

  const [messageLengthWhenSkipped, setMessageLengthWhenSkipped] =
    useState<number>(-1);

  const { botInfo, updateBotInfoConfig } = useBotInfo(config.token);

  const { formattedWelcomeMessage } = useFormattedWelcomeMessage(
    botInfo.bot_info_config.welcome_message,
  );

  const { popupClosedByUser, setPopupClosedByUser } = usePopupClosedByUser(
    botInfo.id,
  );

  const { actions } = useHandleEvents();
  useScheduledActions(actions, botInfo.id, token, onSendRef);

  const isBotDataTrained = useIsBotDataCompletelyTrained(botInfo.id, token);

  const {
    starterQuestions,
    setStarterQuestions,
    loading: loadingStarterQuestions,
    error: errorStarterQuestions,
  } = useStarterQuestions(config.token);

  const {
    chatUserFormFields,
    setChatUserFormFieldsUpdateTime,
    loading: loadingChatUserFormFields,
    error: errorChatUserFormFields,
  } = useChatUserFormFields(config.token);

  useBotVariables(config.token);

  useVariablesMessageListener();

  const { chatId, setChatId, resetChat, isChatReady } = useChatHistory(
    config.token,
    botInfo.id,
    config.chatId,
    eventSource,
    setChatData,
    instanceName,
    setUseWebsocketGeneration,
    setLiveAgentHandoffInfo,
    setHasChatEnded,
  );

  useEvents(
    botInfo,
    config.token,
    updateBotInfoConfig,
    setChatId,
    setStarterQuestions,
    setChatUserFormFieldsUpdateTime,
  );

  useBotInfoUpdates(botInfo);

  const { installedApps } = useInstalledApps(config.token);

  const widgetPosition = openedInDashboard(new URL(config.baseOrigin))
    ? "right"
    : botInfo.bot_info_config.widget_position;

  if (
    !loadingStarterQuestions &&
    !loadingChatUserFormFields &&
    !loadingLocationBasedConsentInfo &&
    isChatReady
  ) {
    setTimeout(
      () => setRenderPositionedWidget(true),
      botInfo.bot_info_config?.widget_position === "right" ? 0 : 100,
    );
  }

  const { isLightAccent, styleColors } = useStyleColors(
    botInfo.bot_info_config.chatbot_color,
    widgetPosition,
  );
  const copyTexts = getChatPanelText(
    botInfo.bot_prompt_config.generation_language,
  );
  const authorizationToken = actions?.[0]?.commands?.[0]?.authorization;
  const inAppMode = actions?.[0]?.commands?.[0]?.inAppMode;
  const workflowLinkedStarterQuestionId =
    actions?.[0]?.commands?.[0]?.starterQuestionId ?? undefined;
  const customChatId = actions?.[0]?.commands?.[0]?.chatId ?? undefined;

  if (inAppMode) {
    setInAppMode(inAppMode ?? false);
  }
  const { connectSSE } = useConnectSSE(
    botInfo?.id || "",
    config,
    botInfo?.bot_info_config?.support_contact_number || null,
    chatId,
    variables,
    setGenerationMode,
    setChatData,
    setHasChatEnded,
    setMessage,
    setEventSource,
    copyTexts,
    setLiveAgentHandoffInfo,
    authorizationToken,
    customChatId,
  );

  const { sendWebsocketMessage } = useConnectWebsocket(
    botInfo.id,
    config,
    botInfo.bot_info_config.support_contact_number,
    chatId,
    setGenerationMode,
    setChatData,
    setMessage,
    useWebsocketGeneration,
    setUseWebsocketGeneration,
    setLiveAgentHandoffInfo,
  );

  usePreserveChatData(instanceName, token, chatData, chatId);

  const chatUserId = useChatUserId(botInfo?.id);
  const useAsPreview = !!config.targetElId;
  const isIframePreview = !!config.isPreviewIframeFromDashboard;
  const isWidgetPreview = !!config.isWidgetPreview;
  const classNameChatPanel = useMemo(() => {
    if (isIframe) {
      return classNames(
        "flex flex-1 flex-col overflow-hidden rounded-xl border border-solid border-supporting-divider/100",
      );
    }
    return classNames(
      "chat-panel-container",
      useAsPreview && "chat-panel-container-preview",
      !widgetExpanded && "chat-panel-container-hidden",
      widgetPosition === "left" && "chat-panel-container-left",
    );
  }, [isIframe, useAsPreview, widgetExpanded, widgetPosition]);

  useEffect(() => {
    if (widgetPosition !== "left") {
      return;
    }
    parentPostMessage("widget-position", "left");
  }, [parentPostMessage, widgetPosition]);

  const processStoredStarterQuestion = useCallback(() => {
    const storedQuestionKey = `starter_question_${botInfo?.id}`;
    const storedQuestionData = getSessionStorageValue(storedQuestionKey);

    if (storedQuestionData) {
      try {
        const questionData = JSON.parse(storedQuestionData);

        if (questionData.type === "follow-up-question") {
          connectSSE(
            questionData.question ?? "",
            chatUserId,
            undefined,
            questionData.is_support_request,
            questionData.integration_config_type,
          )
            .then(setEventSource)
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error(err);
            });
        } else {
          connectSSE(questionData.question ?? "", chatUserId, questionData.id)
            .then(setEventSource)
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error(err);
            });
        }

        // Clear the stored question after processing
        setSessionStorageValue(storedQuestionKey, "");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error processing stored starter question:", err);
      }
    }
  }, [botInfo?.id, chatUserId, connectSSE]);

  const onSend = async (msg: string, chatUserId?: string) => {
    if (!msg || generationMode) {
      return;
    }
    sendAnalyticsEvent("Message Sent", {
      context: JSON.stringify({ message }),
    });

    setMessage("");
    if (useWebsocketGeneration) {
      sendWebsocketMessage(msg ?? "", chatUserId);
      return;
    }
    connectSSE(
      msg ?? "",
      chatUserId,
      workflowLinkedStarterQuestionId ?? undefined,
    )
      .then(setEventSource)
      // eslint-disable-next-line no-console
      .catch(err => console.error(err));
  };

  onSendRef.current = onSend;

  useEffect(() => {
    clearTimeout(timeIdRef.current);
    timeIdRef.current = setTimeout(() => {
      setIsRenderWidget(widgetExpanded);
    }, 100);
  }, [widgetExpanded]);

  const handleSendIntegrationData = ({
    message,
    configType,
    integrationData,
    formData,
  }: {
    message?: string;
    configType: string;
    integrationData: any;
    formData?: { [key: string]: any };
  }): Promise<void> => {
    setMessage("");
    return (
      connectSSE(
        message ?? "",
        chatUserId,
        undefined,
        true,
        configType,
        integrationData,
        formData,
      )
        .then(setEventSource)
        // eslint-disable-next-line no-console
        .catch(err => console.error(err))
    );
  };

  const onSuggestionClick = async (
    afterMessageQuestion: AfterMessageQuestion,
  ) => {
    if (!widgetExpanded) {
      setWidgetExpanded(true);
    }

    // Check if user form is required and user is not authenticated
    const hasFormItems =
      chatUserFormFields &&
      Array.isArray(chatUserFormFields?.data_fields) &&
      !!chatUserFormFields?.data_fields?.filter(item => !item.is_deleted)
        .length;

    const isUserFormRequired =
      hasFormItems &&
      !getLocalStorageValue(getUserDataStorageKey(instanceName, botInfo.id)) &&
      !config.hideUserForm &&
      !botInfo.bot_info_config.hide_user_form;

    if (isUserFormRequired) {
      // Store starter question in session storage
      // eslint-disable-next-line no-console
      console.log("afterMessageQuestion", afterMessageQuestion);
      if (afterMessageQuestion.type === "follow-up-question") {
        setSessionStorageValue(
          `starter_question_${botInfo.id}`,
          JSON.stringify({
            question: afterMessageQuestion.question,
            is_support_request: afterMessageQuestion.is_support_request,
            integration_config_type:
              afterMessageQuestion.integration_config_type,
            type: afterMessageQuestion.type,
          }),
        );
      } else {
        const question = replaceVariablesInString(
          afterMessageQuestion.question,
          variables,
        );
        setSessionStorageValue(
          `starter_question_${botInfo.id}`,
          JSON.stringify({
            question,
            id: afterMessageQuestion.id,
            type: afterMessageQuestion.type,
          }),
        );
      }
      // Exit early - the form submission will handle the message after authentication
      return;
    }

    if (afterMessageQuestion.type === "follow-up-question") {
      return (
        connectSSE(
          afterMessageQuestion.question ?? "",
          chatUserId,
          workflowLinkedStarterQuestionId ?? undefined,
          afterMessageQuestion.is_support_request,
          afterMessageQuestion.integration_config_type,
        )
          .then(setEventSource)
          // eslint-disable-next-line no-console
          .catch(err => console.error(err))
      );
    } else {
      const question = replaceVariablesInString(
        afterMessageQuestion.question,
        variables,
      );
      sendAnalyticsEvent("Starter Question Clicked", {
        context: JSON.stringify({
          question,
        }),
      });
      return (
        connectSSE(
          question ?? "",
          chatUserId,
          afterMessageQuestion.id ?? undefined,
        )
          .then(setEventSource)
          // eslint-disable-next-line no-console
          .catch(err => console.error(err))
      );
    }
  };

  const anyError = errorStarterQuestions || errorChatUserFormFields;

  if (!widgetOpen || !renderPositionedWidget) {
    return null;
  }

  const showPopupMessages =
    !widgetExpanded &&
    (botInfo.bot_info_config.show_starter_widget ||
      !!botInfo.bot_info_config.welcome_message);

  const handleCancelHumanHandoff = async () => {
    await sendWebsocketMessage("", chatUserId, {
      cancel_handoff: true,
    });
    setLiveAgentHandoffInfo(prev => {
      return {
        ...prev,
        is_active: false,
        is_agent_connected: false,
      };
    });
    setUseWebsocketGeneration(false);
  };

  return (
    <div
      className={classNames(
        useAsPreview ? "bot-container-preview" : "",
        isIframe ? "bot-container_standalone" : "bot-container",
        widgetPosition === "left" && !isIframe && "bot-container-left",
      )}
      style={styleColors}
    >
      {anyError && <div className="max-w-[200px] text-ws-red">{anyError}</div>}
      {(isRenderWidget || isIframe) && (
        // maxwidth 920px
        <TransitionGroup
          className={
            isIframe ? "flex w-full max-w-[920px] flex-col py-0 md:py-8" : ""
          }
        >
          <CSSTransition
            nodeRef={wrapperRef}
            timeout={200}
            classNames="transition-wrapper"
            in
          >
            <div
              id="chat-panel"
              className={classNameChatPanel}
              ref={wrapperRef}
            >
              <ChatPanel
                isBotDataTrained={isBotDataTrained}
                botInfo={botInfo}
                message={message ?? ""}
                generationMode={generationMode}
                setGenerationMode={setGenerationMode}
                setMessage={setMessage}
                onSend={onSend}
                processStoredStarterQuestion={processStoredStarterQuestion}
                chatData={chatData}
                setChatData={setChatData}
                resetChat={resetChat}
                useAsPreview={useAsPreview}
                isIframePreview={isIframePreview}
                isWidgetPreview={isWidgetPreview}
                afterMessageQuestions={starterQuestions.map(
                  starterQuestion => ({
                    type: "starter-question",
                    ...starterQuestion,
                  }),
                )}
                config={config}
                chatId={chatId}
                eventSource={eventSource}
                chatUserFormFields={chatUserFormFields}
                loadingChatUserFormFields={loadingChatUserFormFields}
                baseURL={config.serviceBaseUrl}
                token={config.token}
                widgetPosition={widgetPosition}
                onSuggestionClick={onSuggestionClick}
                locationBasedConsentInfo={locationBasedConsentInfo}
                messageLengthWhenSkipped={messageLengthWhenSkipped}
                setMessageLengthWhenSkipped={setMessageLengthWhenSkipped}
                installedApps={installedApps || []}
                setUseWebsocketGeneration={setUseWebsocketGeneration}
                welcomeMessage={formattedWelcomeMessage}
                actions={actions}
                liveAgentHandoffInfo={liveAgentHandoffInfo}
                setLiveAgentHandoffInfo={setLiveAgentHandoffInfo}
                handleCancelHumanHandoff={handleCancelHumanHandoff}
                handleSendIntegrationData={handleSendIntegrationData}
                hasChatEnded={hasChatEnded}
              />
            </div>
          </CSSTransition>
        </TransitionGroup>
      )}

      {/* do not show when standalone */}
      {(showPopupMessages || botInfo.bot_info_config.welcome_message) &&
        !isIframe && (
          <PopupMessages
            botInfo={botInfo}
            afterMessageQuestions={starterQuestions.map(starterQuestion => ({
              type: "starter-question",
              ...starterQuestion,
            }))}
            widgetPosition={widgetPosition}
            onSuggestionClick={onSuggestionClick}
            popupClosedByUser={popupClosedByUser}
            setPopupClosedByUser={setPopupClosedByUser}
            chatDataLength={chatData.length}
            generationMode={generationMode}
            isOverButton
            welcomeMessage={formattedWelcomeMessage}
          />
        )}

      {!isIframe && (
        <WidgetButton
          botInfo={botInfo}
          widgetPosition={widgetPosition}
          useAsPreview={useAsPreview}
          isLightAccent={isLightAccent}
          chatHasMessages={chatData.length > 1}
        />
      )}
    </div>
  );
};

export default SonicBot;
